/**
 * API client for planit.family backend
 * Base URL: http://localhost:3001
 */

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000';

export class ApiError extends Error {
  constructor(message: string, public status: number, public data?: unknown) {
    super(message);
    this.name = 'ApiError';
  }
}

interface FetchOptions extends RequestInit {
  params?: Record<string, string>;
}

// --- Refresh lock: ensures only one refresh request at a time ---
let isRefreshing = false;
let refreshSubscribers: Array<(ok: boolean) => void> = [];

function onRefreshDone(ok: boolean) {
  refreshSubscribers.forEach((cb) => cb(ok));
  refreshSubscribers = [];
}

async function waitForRefresh(): Promise<boolean> {
  return new Promise((resolve) => {
    refreshSubscribers.push(resolve);
  });
}

async function doRefresh(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE_URL}/api/v1/auth/session/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({}),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Base fetch wrapper with error handling, cookie support, and auto-refresh
 */
async function fetchApi<T>(
  endpoint: string,
  options: FetchOptions = {}
): Promise<T> {
  const { params, ...fetchOptions } = options;

  // Build URL with query params
  let url = `${API_BASE_URL}${endpoint}`;
  if (params) {
    const searchParams = new URLSearchParams(params);
    url += `?${searchParams.toString()}`;
  }

  // Default options
  const config: RequestInit = {
    credentials: 'include', // Include cookies
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      ...fetchOptions.headers,
    },
    ...fetchOptions,
  };

  try {
    let response = await fetch(url, config);

    // Auto-refresh on 401 (skip for the refresh endpoint itself)
    if (
      response.status === 401 &&
      !endpoint.includes('/auth/session/refresh')
    ) {
      let refreshOk: boolean;

      if (isRefreshing) {
        // Another request is already refreshing — wait for it
        refreshOk = await waitForRefresh();
      } else {
        isRefreshing = true;
        refreshOk = await doRefresh();
        isRefreshing = false;
        onRefreshDone(refreshOk);
      }

      if (refreshOk) {
        // Retry the original request with fresh cookies
        response = await fetch(url, config);
      }
    }

    // Handle non-2xx responses
    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = { message: response.statusText };
      }

      throw new ApiError(
        errorData.message || `HTTP ${response.status}`,
        response.status,
        errorData
      );
    }

    // Handle no content
    if (response.status === 204) {
      return undefined as T;
    }

    // Parse JSON response
    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(
      error instanceof Error ? error.message : 'Network error',
      0
    );
  }
}

// Auth API
export const authApi = {
  async signup(email: string, password: string, name: string, familyName?: string) {
    return fetchApi('/api/v1/auth/email/signup', {
      method: 'POST',
      body: JSON.stringify({ email, password, name, ...(familyName ? { familyName } : {}) }),
    });
  },

  async login(email: string, password: string) {
    return fetchApi('/api/v1/auth/email/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },

  async logout() {
    return fetchApi('/api/v1/auth/session/logout', {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },

  async refreshToken() {
    return fetchApi('/api/v1/auth/session/refresh', {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },

  async switchFamily(familyId: string) {
    return fetchApi('/api/v1/auth/session/switch-family', {
      method: 'POST',
      body: JSON.stringify({ familyId }),
    });
  },

  async getSession() {
    return fetchApi<{
      user: {
        _id: string;
        email: string;
        name?: string;
        familyId?: string;
        emailVerified: boolean;
      };
      family?: {
        _id: string;
        name: string;
        members: Array<{
          userId: string;
          email: string;
          name?: string;
          role: string;
        }>;
      };
    }>('/api/v1/auth/session/me');
  },

  async resendVerification() {
    return fetchApi<{ message: string }>('/api/v1/auth/email/resend-verification', {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },

  getGoogleAuthUrl() {
    return `${API_BASE_URL}/api/v1/auth/google`;
  },

  getGoogleCalendarAuthUrl() {
    return `${API_BASE_URL}/api/v1/auth/google/calendar`;
  },
};

// Family member and invite types
export interface FamilyMemberDetailed {
  id: string;
  familyId: string;
  userId?: string;
  type: 'account' | 'managed';
  role: 'admin' | 'member';
  displayName: string;
  preferences: {
    dietaryRestrictions: string[];
    dislikes: string[];
    notifications: {
      mealPlanReminders: boolean;
      shoppingListUpdates: boolean;
    };
  };
  createdAt: string;
  updatedAt: string;
}

export interface FamilyInvite {
  id: string;
  familyId: string;
  email: string;
  role: 'admin' | 'member';
  expiresAt: string;
  createdAt: string;
}

export interface CreateInviteResponse extends FamilyInvite {
  token: string;
}

export interface WeeklyStaple {
  name: string;
  department: string;
  defaultQuantity?: number;
  defaultUnit?: string;
  enabled: boolean;
}

export interface FamilySettings {
  timezone: string;
  weekStartsOn: number;
  defaultServings: number;
  mealTypes: string[];
  dinnerWindow: { start: string; end: string };
  includeSharedRecipesInSuggestions?: boolean;
  weeklyStaples?: WeeklyStaple[];
}

export interface FamilyDetail {
  id: string;
  name: string;
  slug: string;
  settings: FamilySettings;
  createdAt: string;
  updatedAt: string;
}

export interface FamilyInviteDetail {
  id: string;
  familyId: string;
  familyName: string;
  email: string;
  role: 'admin' | 'member';
  expiresAt: string;
}

// Family API
export const familyApi = {
  async create(name: string) {
    return fetchApi('/api/v1/families', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  },

  async get(familyId: string) {
    return fetchApi<FamilyDetail>(`/api/v1/families/${familyId}`);
  },

  async update(familyId: string, body: { name?: string; settings?: Partial<FamilySettings> }) {
    return fetchApi<FamilyDetail>(`/api/v1/families/${familyId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },

  async getMembers(familyId: string) {
    return fetchApi<FamilyMemberDetailed[]>(`/api/v1/families/${familyId}/members`);
  },

  async updateMember(familyId: string, memberId: string, body: { role?: 'admin' | 'member'; displayName?: string }) {
    return fetchApi<FamilyMemberDetailed>(`/api/v1/families/${familyId}/members/${memberId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },

  async removeMember(familyId: string, memberId: string) {
    return fetchApi<void>(`/api/v1/families/${familyId}/members/${memberId}`, {
      method: 'DELETE',
    });
  },

  async createInvite(familyId: string, body: { email: string; role?: 'admin' | 'member' }) {
    return fetchApi<CreateInviteResponse>(`/api/v1/families/${familyId}/invites`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  async listInvites(familyId: string) {
    return fetchApi<FamilyInvite[]>(`/api/v1/families/${familyId}/invites`);
  },

  async revokeInvite(familyId: string, inviteId: string) {
    return fetchApi<void>(`/api/v1/families/${familyId}/invites/${inviteId}`, {
      method: 'DELETE',
    });
  },

  async createManagedMember(familyId: string, displayName: string) {
    return fetchApi<FamilyMemberDetailed>(`/api/v1/families/${familyId}/managed-members`, {
      method: 'POST',
      body: JSON.stringify({ displayName }),
    });
  },
};

// Invite API (public token-based endpoints)
export const inviteApi = {
  async getByToken(token: string) {
    return fetchApi<FamilyInviteDetail>(`/api/v1/invites/${token}`);
  },

  async accept(token: string, displayName: string) {
    return fetchApi<FamilyMemberDetailed>(`/api/v1/invites/${token}/accept`, {
      method: 'POST',
      body: JSON.stringify({ displayName }),
    });
  },
};

// iCal Subscription types
export interface ICalSyncState {
  status: 'active' | 'error' | 'paused';
  lastSyncAt?: string;
  errorCount: number;
  lastError?: string;
  nextSyncAt?: string;
}

export interface ICalSubscription {
  id: string;
  familyId: string;
  memberId: string;
  name: string;
  url: string;
  color?: string;
  syncState: ICalSyncState;
  createdAt: string;
  updatedAt: string;
}

export const icalSubscriptionApi = {
  async list(familyId: string) {
    return fetchApi<ICalSubscription[]>(`/api/v1/families/${familyId}/ical-subscriptions`);
  },

  async create(familyId: string, body: { memberId: string; name: string; url: string; color?: string }) {
    return fetchApi<ICalSubscription>(`/api/v1/families/${familyId}/ical-subscriptions`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  async update(familyId: string, subscriptionId: string, body: { name?: string; color?: string }) {
    return fetchApi<ICalSubscription>(`/api/v1/families/${familyId}/ical-subscriptions/${subscriptionId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },

  async delete(familyId: string, subscriptionId: string) {
    return fetchApi<void>(`/api/v1/families/${familyId}/ical-subscriptions/${subscriptionId}`, {
      method: 'DELETE',
    });
  },

  async sync(familyId: string, subscriptionId: string) {
    return fetchApi<{ message: string; synced: number }>(
      `/api/v1/families/${familyId}/ical-subscriptions/${subscriptionId}/sync`,
      { method: 'POST', body: JSON.stringify({}) }
    );
  },
};

// School Lunch Subscription types
export interface SchoolLunchSyncState {
  status: 'active' | 'error' | 'paused';
  lastSyncAt?: string;
  errorCount: number;
  lastError?: string;
  nextSyncAt?: string;
}

export interface SchoolLunchSubscription {
  id: string;
  familyId: string;
  memberId: string;
  name: string;
  url: string;
  provider: string;
  syncFrequency: 'weekly' | 'monthly';
  color?: string;
  syncState: SchoolLunchSyncState;
  createdAt: string;
  updatedAt: string;
}

export interface SchoolLunchMenuItem {
  name: string;
  category?: string;
  foodCategory?: string;
  description?: string;
  ingredients?: string;
  nutrition?: Record<string, number>;
  isSectionTitle?: boolean;
}

export interface SchoolLunchMenu {
  id: string;
  subscriptionId: string;
  familyId: string;
  memberId: string;
  date: string;
  isNoSchool: boolean;
  noSchoolReason?: string;
  menuItems: SchoolLunchMenuItem[];
}

export const schoolLunchApi = {
  async list(familyId: string) {
    return fetchApi<SchoolLunchSubscription[]>(
      `/api/v1/families/${familyId}/school-lunch-subscriptions`
    );
  },

  async create(
    familyId: string,
    body: {
      memberId: string;
      name: string;
      url: string;
      syncFrequency?: 'weekly' | 'monthly';
      color?: string;
    }
  ) {
    return fetchApi<SchoolLunchSubscription>(
      `/api/v1/families/${familyId}/school-lunch-subscriptions`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      }
    );
  },

  async update(
    familyId: string,
    subscriptionId: string,
    body: { name?: string; syncFrequency?: 'weekly' | 'monthly'; color?: string }
  ) {
    return fetchApi<SchoolLunchSubscription>(
      `/api/v1/families/${familyId}/school-lunch-subscriptions/${subscriptionId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(body),
      }
    );
  },

  async delete(familyId: string, subscriptionId: string) {
    return fetchApi<void>(
      `/api/v1/families/${familyId}/school-lunch-subscriptions/${subscriptionId}`,
      { method: 'DELETE' }
    );
  },

  async sync(familyId: string, subscriptionId: string) {
    return fetchApi<{ message: string; synced: number }>(
      `/api/v1/families/${familyId}/school-lunch-subscriptions/${subscriptionId}/sync`,
      { method: 'POST', body: JSON.stringify({}) }
    );
  },

  async getMenus(familyId: string, start: string, end: string, memberId?: string) {
    const params: Record<string, string> = { start, end };
    if (memberId) params.memberId = memberId;
    return fetchApi<SchoolLunchMenu[]>(
      `/api/v1/families/${familyId}/school-lunch-menus`,
      { params }
    );
  },
};

// Calendar API types matching backend schemas
export interface CalendarConnectionResponse {
  id: string;
  provider: 'google';
  calendars: Array<{
    calendarId: string;
    name: string;
    primary: boolean;
    selected: boolean;
    color?: string;
  }>;
  syncState: {
    status: 'active' | 'paused' | 'error' | 'disabled';
    lastSyncAt?: string;
    errorCount: number;
    lastError?: string;
  };
  hasCalendarScope: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AvailableCalendar {
  id: string;
  summary: string;
  primary: boolean;
  accessRole: string;
  backgroundColor?: string;
  selected?: boolean;
}

export const calendarApi = {
  async getEvents(start: string, end: string) {
    return fetchApi<{
      events: Array<{
        id: string;
        calendarId: string;
        summary: string;
        start: string;
        end: string;
        description?: string;
        location?: string;
        isAllDay: boolean;
        timezone?: string;
        isRecurring: boolean;
        status: string;
        htmlLink?: string;
      }>;
      calendars: Array<{
        calendarId: string;
        name: string;
        color?: string;
      }>;
    }>('/api/v1/calendar/events', {
      params: { start, end },
    });
  },

  async getConnection() {
    return fetchApi<CalendarConnectionResponse>('/api/v1/calendar/connection');
  },

  async updateConnection(selectedCalendars: string[], allCalendarIds: string[]) {
    // Backend expects { selections: { calendarId, selected }[] }
    // Send all calendars with their selected status to handle deselection
    const selections = allCalendarIds.map((calendarId) => ({
      calendarId,
      selected: selectedCalendars.includes(calendarId),
    }));
    return fetchApi<CalendarConnectionResponse>('/api/v1/calendar/connection', {
      method: 'PUT',
      body: JSON.stringify({ selections }),
    });
  },

  async listCalendars() {
    // Returns array directly, not wrapped in { calendars }
    return fetchApi<AvailableCalendar[]>('/api/v1/calendar/list');
  },

  async deleteConnection(removeEvents = false) {
    return fetchApi<void>(
      `/api/v1/calendar/connection${removeEvents ? '?removeEvents=true' : ''}`,
      { method: 'DELETE' }
    );
  },

  async sync() {
    return fetchApi<SyncResult>('/api/v1/calendar/sync', {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },
};

// Sync result types
export interface SyncConflict {
  date: string;          // YYYY-MM-DD
  oldType: string;       // previous NightType (was easy_cooking)
  newType: string;       // new NightType
  reason: string;
  plannedMealId: string;
}

export interface SyncOpportunity {
  date: string;    // YYYY-MM-DD
  oldType: string; // previous (busy) NightType
}

export interface SyncResult {
  message: string;
  googleSynced: number;
  icalSynced: number;
  nightsReclassified: number;
  conflicts: SyncConflict[];
  opportunities: SyncOpportunity[];
}

// Night Classification API types
export type NightType = 'easy_cooking' | 'dinner_out' | 'fend_for_yourself';

export interface MemberConflict {
  memberId: string;
  displayName: string;
  hasConflict: boolean;
  conflictingEvents: string[];
}

export interface NightClassification {
  id: string;
  familyId: string;
  date: string;
  type: NightType;
  confidence: number;
  reason: string;
  isOverride: boolean;
  events: string[];
  memberConflicts: MemberConflict[];
  createdAt: string;
  updatedAt: string;
}

export interface WeeklyClassificationResponse {
  startDate: string;
  endDate: string;
  familyTimezone: string;
  classifications: NightClassification[];
}

export const nightClassificationApi = {
  async getDaily(date: string) {
    return fetchApi<NightClassification>(
      '/api/v1/calendar/night-classification/daily',
      {
        params: { date },
      }
    );
  },

  async getWeekly(startDate: string) {
    return fetchApi<WeeklyClassificationResponse>(
      '/api/v1/calendar/night-classification/weekly',
      {
        params: { startDate },
      }
    );
  },

  async override(date: string, type: NightType, reason?: string) {
    return fetchApi<NightClassification>(
      '/api/v1/calendar/night-classification/override',
      {
        method: 'POST',
        body: JSON.stringify({ date, type, reason }),
      }
    );
  },

  async triggerClassification(startDate: string, endDate?: string) {
    return fetchApi<WeeklyClassificationResponse>(
      '/api/v1/calendar/night-classification/classify',
      {
        method: 'POST',
        body: JSON.stringify({ startDate, endDate }),
      }
    );
  },
};

// Recipe API types
export type MealType = 'breakfast' | 'lunch' | 'dinner' | 'snack' | 'dessert' | 'side';

export interface Recipe {
  id: string;
  familyId: string;
  name: string;
  description?: string;
  imageUrl?: string;
  totalTime?: number;
  servings: number;
  tags: string[];
  mealType: MealType[];
  difficulty?: 'easy' | 'medium' | 'hard';
  isArchived: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface RecipeWithStats {
  recipe: Recipe;
  lastMadeAt?: string;
  daysSinceLastMade?: number;
}

export interface RecipeListResponse {
  recipes: RecipeWithStats[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasMore: boolean;
  };
}

export interface RecipeListOptions {
  search?: string;
  tags?: string;
  cuisine?: string;
  mealType?: MealType;
  difficulty?: string;
  maxTime?: number;
  includeArchived?: boolean;
  page?: number;
  limit?: number;
  sortBy?: 'name' | 'createdAt' | 'updatedAt' | 'totalTime';
  sortOrder?: 'asc' | 'desc';
  withStats?: boolean;
}

// Recipe detail types (returned by GET /api/v1/recipes/:id)
export interface RecipeIngredient {
  quantity?: number;
  unit?: string;
  item: string;
  normalizedItem: string;
  preparation?: string;
  optional?: boolean;
  group?: string;
}

export interface RecipeInstruction {
  step: number;
  text: string;
  duration?: number;
}

export interface RecipeSource {
  type: 'url' | 'manual' | 'imported';
  url?: string;
  siteName?: string;
  importedAt?: string;
}

export interface RecipeDetail extends Recipe {
  ingredients: RecipeIngredient[];
  instructions: RecipeInstruction[];
  prepTime?: number;
  cookTime?: number;
  servingsUnit?: string;
  cuisine?: string;
  source: RecipeSource;
  createdBy: string;
}

// AI Import types
export interface AiImportIngredient {
  quantity?: number;
  unit?: string;
  item: string;
  preparation?: string;
  optional?: boolean;
  group?: string;
}

export interface AiImportInstruction {
  step: number;
  text: string;
}

export interface AiImportRecipe {
  name: string;
  description?: string;
  imageUrl?: string;
  ingredients: AiImportIngredient[];
  instructions: AiImportInstruction[];
  prepTime?: number;
  cookTime?: number;
  totalTime?: number;
  servings?: number;
  tags?: string[];
  cuisine?: string;
  mealType?: MealType[];
  difficulty?: 'easy' | 'medium' | 'hard';
  siteName?: string;
}

export interface AiImportResponse {
  recipe: AiImportRecipe;
  sourceUrl: string;
}

export interface CreateRecipeInput {
  name: string;
  description?: string;
  imageUrl?: string;
  ingredients: AiImportIngredient[];
  instructions: AiImportInstruction[];
  prepTime?: number;
  cookTime?: number;
  totalTime?: number;
  servings?: number;
  servingsUnit?: string;
  tags?: string[];
  cuisine?: string;
  mealType?: MealType[];
  difficulty?: 'easy' | 'medium' | 'hard';
  source?: {
    type: 'url' | 'manual' | 'imported';
    url?: string;
    siteName?: string;
  };
}

export const recipeApi = {
  async list(options: RecipeListOptions = {}) {
    const params: Record<string, string> = {};
    if (options.search) params.search = options.search;
    if (options.tags) params.tags = options.tags;
    if (options.cuisine) params.cuisine = options.cuisine;
    if (options.mealType) params.mealType = options.mealType;
    if (options.difficulty) params.difficulty = options.difficulty;
    if (options.maxTime !== undefined) params.maxTime = String(options.maxTime);
    if (options.includeArchived !== undefined) params.includeArchived = String(options.includeArchived);
    if (options.page !== undefined) params.page = String(options.page);
    if (options.limit !== undefined) params.limit = String(options.limit);
    if (options.sortBy) params.sortBy = options.sortBy;
    if (options.sortOrder) params.sortOrder = options.sortOrder;
    if (options.withStats !== undefined) params.withStats = String(options.withStats);

    return fetchApi<RecipeListResponse>('/api/v1/recipes', { params });
  },

  async get(id: string) {
    return fetchApi<RecipeDetail>(`/api/v1/recipes/${id}`);
  },

  async update(id: string, input: Partial<CreateRecipeInput>) {
    return fetchApi<RecipeDetail>(`/api/v1/recipes/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  },

  async archive(id: string) {
    return fetchApi<void>(`/api/v1/recipes/${id}`, {
      method: 'DELETE',
    });
  },

  async aiImport(url: string, browser?: 'local' | 'browserless-rest' | 'browserless-playwright') {
    return fetchApi<AiImportResponse>('/api/v1/recipes/ai-import', {
      method: 'POST',
      body: JSON.stringify({ url, browser }),
    });
  },

  async create(input: CreateRecipeInput) {
    return fetchApi<Recipe>('/api/v1/recipes', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
};

// Shared recipes API (browse global catalog)
export interface SharedRecipeSummary {
  id: string;
  name: string;
  description?: string;
  imageUrl?: string;
  totalTime?: number;
  tags: string[];
  mealType: MealType[];
  difficulty?: 'easy' | 'medium' | 'hard';
}

export interface SharedRecipeDetail {
  id: string;
  name: string;
  description?: string;
  imageUrl?: string;
  ingredients: RecipeIngredient[];
  instructions: RecipeInstruction[];
  prepTime?: number;
  cookTime?: number;
  totalTime?: number;
  servings: number;
  servingsUnit?: string;
  tags: string[];
  cuisine?: string;
  mealType: MealType[];
  difficulty?: 'easy' | 'medium' | 'hard';
  source: RecipeSource;
  createdAt: string;
  updatedAt: string;
}

export interface SharedRecipeListResponse {
  recipes: SharedRecipeSummary[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasMore: boolean;
  };
}

export const sharedRecipesApi = {
  async browse(options: { search?: string; mealType?: string; limit?: number; page?: number } = {}) {
    const params: Record<string, string> = {};
    if (options.search) params.search = options.search;
    if (options.mealType) params.mealType = options.mealType;
    if (options.limit !== undefined) params.limit = String(options.limit);
    if (options.page !== undefined) params.page = String(options.page);
    return fetchApi<SharedRecipeListResponse>('/api/v1/shared-recipes', { params });
  },

  async get(id: string) {
    return fetchApi<SharedRecipeDetail>(`/api/v1/shared-recipes/${id}`);
  },

  async importToFamily(id: string) {
    return fetchApi<Recipe>(`/api/v1/shared-recipes/${id}/import`, {
      method: 'POST',
    });
  },
};

// Meal History API types
export type MealHistoryMealType = 'breakfast' | 'lunch' | 'dinner';
export type MealHistorySource = 'manual' | 'meal_plan';
export type MealHistoryStatus = 'planned' | 'cooked' | 'deviated';
export type MealHistoryDeviationType = 'different_recipe' | 'ate_out' | 'leftovers' | 'ordered_in';
export type RecipeRole = 'main' | 'side' | 'dessert';

export interface MealRecipeEntry {
  recipeId: string;
  role: RecipeRole;
  recipe?: {
    id: string;
    name: string;
    imageUrl?: string;
    totalTime?: number;
  };
}

export interface MealHistory {
  id: string;
  familyId: string;
  /** @deprecated Use recipes array. Kept for backwards compat. */
  recipeId?: string;
  /** @deprecated Use recipes array. Kept for backwards compat. */
  recipe?: {
    id: string;
    name: string;
    imageUrl?: string;
    totalTime?: number;
  };
  recipes: MealRecipeEntry[];
  date: string;
  mealType: MealHistoryMealType;
  servings: number;
  notes?: string;
  source: MealHistorySource;
  status: MealHistoryStatus;
  deviationType?: MealHistoryDeviationType;
  actualRecipeId?: string;
  actualRecipe?: {
    id: string;
    name: string;
    imageUrl?: string;
    totalTime?: number;
  };
  rating?: number;
  createdAt: string;
  updatedAt: string;
}

export interface MealHistoryListResponse {
  entries: MealHistory[];
}

export interface RecipeEntryInput {
  recipeId: string;
  role: RecipeRole;
}

export interface CreateMealHistoryInput {
  recipeId?: string;
  recipes?: RecipeEntryInput[];
  date: string;
  mealType?: MealHistoryMealType;
  servings?: number;
  notes?: string;
  source?: MealHistorySource;
  status?: MealHistoryStatus;
  deviationType?: MealHistoryDeviationType;
  rating?: number;
}

export interface UpdateMealHistoryInput {
  recipeId?: string;
  recipes?: RecipeEntryInput[];
  date?: string;
  mealType?: MealHistoryMealType;
  servings?: number;
  notes?: string;
  status?: MealHistoryStatus;
  deviationType?: MealHistoryDeviationType;
  actualRecipeId?: string;
  rating?: number;
}

export interface LogMealInput {
  outcome: 'cooked' | 'deviated';
  deviationType?: MealHistoryDeviationType;
  actualRecipeId?: string;
  rating?: number;
  notes?: string;
}

// Meal Suggestions API types

export interface SuggestedRecipe {
  _id: string;
  name: string;
  imageUrl?: string;
  totalTime?: number;
  difficulty?: 'easy' | 'medium' | 'hard';
  tags: string[];
}

export interface SuggestionItem {
  recipe: SuggestedRecipe;
  score: number;
  avgRating?: number;
  daysSinceLastMade?: number;
  source?: 'family' | 'shared';
  sharedRecipeId?: string;
}

export interface DaySuggestion {
  date: string; // YYYY-MM-DD
  nightType: NightType;
  items: SuggestionItem[];
}

export interface WeeklyMealSuggestionsResponse {
  suggestions: DaySuggestion[];
}

export const mealSuggestionsApi = {
  async getWeekly(startDate: string): Promise<WeeklyMealSuggestionsResponse> {
    return fetchApi<WeeklyMealSuggestionsResponse>(
      '/api/v1/meal-suggestions/weekly',
      { params: { startDate } }
    );
  },
};

export const mealHistoryApi = {
  async list(startDate: string, endDate: string, options?: { status?: MealHistoryStatus; mealType?: MealHistoryMealType }) {
    const params: Record<string, string> = { startDate, endDate };
    if (options?.status) params.status = options.status;
    if (options?.mealType) params.mealType = options.mealType;

    return fetchApi<MealHistoryListResponse>('/api/v1/meal-history', { params });
  },

  async create(input: CreateMealHistoryInput) {
    return fetchApi<MealHistory>('/api/v1/meal-history', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  async assignShared(sharedRecipeId: string, date: string, mealType: MealHistoryMealType = 'dinner', role: RecipeRole = 'main') {
    return fetchApi<MealHistory>('/api/v1/meal-history/assign-shared', {
      method: 'POST',
      body: JSON.stringify({ sharedRecipeId, date, mealType, role }),
    });
  },

  async get(id: string) {
    return fetchApi<MealHistory>(`/api/v1/meal-history/${id}`);
  },

  async update(id: string, input: UpdateMealHistoryInput) {
    return fetchApi<MealHistory>(`/api/v1/meal-history/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  },

  async delete(id: string) {
    return fetchApi<void>(`/api/v1/meal-history/${id}`, {
      method: 'DELETE',
    });
  },

  async markAsCooked(id: string) {
    return fetchApi<MealHistory>(`/api/v1/meal-history/${id}/mark-cooked`, {
      method: 'POST',
    });
  },

  async log(id: string, input: LogMealInput) {
    return fetchApi<MealHistory>(`/api/v1/meal-history/${id}/log`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
};

// ============================================================
// Shopping Lists
// ============================================================

export interface ShoppingListItem {
  displayName: string;
  quantity?: number;
  unit?: string;
  optional: boolean;
  department: string;
  source?: 'recipe' | 'weekly_staple';
}

export interface ShoppingListPantryStaple {
  displayName: string;
  quantity?: number;
  unit?: string;
  optional: boolean;
  suggestedDepartment: string;
}

export interface ShoppingList {
  id: string;
  familyId: string;
  startDate: string;
  endDate: string;
  label?: string;
  items: ShoppingListItem[];
  pantryStaples: ShoppingListPantryStaple[];
  createdAt: string;
  updatedAt: string;
}

export interface ShoppingListSummary {
  id: string;
  startDate: string;
  endDate: string;
  label?: string;
  createdAt: string;
}

export interface ShoppingListListResponse {
  lists: ShoppingListSummary[];
}

export interface AddPantryItemInput {
  displayName: string;
  quantity?: number;
  unit?: string;
  optional?: boolean;
  department: string;
}

export interface DefaultStaple {
  name: string;
  department: string;
  defaultQuantity?: number;
  defaultUnit?: string;
}

export interface DefaultStaplesResponse {
  staples: DefaultStaple[];
  departments: string[];
}

export const shoppingListsApi = {
  async getDefaultStaples(): Promise<DefaultStaplesResponse> {
    return fetchApi<DefaultStaplesResponse>('/api/v1/shopping-lists/default-staples');
  },

  async generate(startDate: string, endDate: string): Promise<ShoppingList> {
    return fetchApi<ShoppingList>('/api/v1/shopping-lists/generate', {
      method: 'POST',
      body: JSON.stringify({ startDate, endDate }),
    });
  },

  async list(limit?: number): Promise<ShoppingListListResponse> {
    const params = limit != null ? { limit: String(limit) } : undefined;
    return fetchApi<ShoppingListListResponse>('/api/v1/shopping-lists', { params });
  },

  async get(id: string): Promise<ShoppingList> {
    return fetchApi<ShoppingList>(`/api/v1/shopping-lists/${id}`);
  },

  async addPantryItem(listId: string, item: AddPantryItemInput): Promise<ShoppingList> {
    return fetchApi<ShoppingList>(`/api/v1/shopping-lists/${listId}`, {
      method: 'PATCH',
      body: JSON.stringify(item),
    });
  },
};

// ============================================================
// Reporting
// ============================================================

export interface CookedRecipe {
  recipeId: string;
  name: string;
  count: number;
  lastCooked: string;
}

export interface RatedRecipe {
  recipeId: string;
  name: string;
  avgRating: number;
  cookCount: number;
}

export interface ReportingSummary {
  period: {
    startDate: string;
    endDate: string;
  };
  mealPlanAdherence: {
    totalPlanned: number;
    totalCooked: number;
    totalDeviated: number;
    totalMissed: number;
    adherenceRate: number;
    estimatedWastedMeals: number;
    deviationBreakdown: {
      differentRecipe: number;
      ateOut: number;
      leftovers: number;
      orderedIn: number;
    };
  };
  topRecipes: {
    mostCooked: CookedRecipe[];
    highestRated: RatedRecipe[];
  };
  nightBreakdown: {
    easyCooking: number;
    dinnerOut: number;
    fendForYourself: number;
    total: number;
  };
  calendarSummary: {
    totalEvents: number;
    averageEventsPerDay: number;
  };
}

export const reportingApi = {
  async getSummary(startDate: string, endDate: string): Promise<ReportingSummary> {
    return fetchApi<ReportingSummary>('/api/v1/reporting/summary', {
      params: { startDate, endDate },
    });
  },
};
